/**
 * ZLAYT-1519: the browser half of the request id.
 *
 * Every API call carries an `X-Request-ID` (12 hex characters). The server
 * adopts it, writes it on every log line for that call and on the report
 * worker's lines for any run the call starts, and returns it in the error
 * body (`error.request_id`). When a call fails, its id is kept for a short
 * while so the error toast can show "Ref: XXXX": a screenshot of the error
 * then leads straight to what the server did. Zlayt PM has worked this way
 * since September 2026.
 *
 * The header goes only to this app's API. S3 and other hosts would have to
 * allow it in their CORS rules, so it is never sent to them.
 *
 * It is sent only after a response from the API has carried one. The browser
 * can read that header only when the backend exposes it, which is the same
 * backend release that allows it in CORS; before that, sending it would fail
 * every preflight. So this build is safe to ship before or after the backend.
 */
import type { InternalAxiosRequestConfig } from 'axios';

export const REQUEST_ID_HEADER = 'X-Request-ID';

/** How long a failure's id is offered to the next error message. */
const RECENT_FAILURE_MS = 15_000;
const VALID_ID = /^[A-Za-z0-9-]{6,64}$/;

let lastFailure: { id: string; at: number } | null = null;
let apiAcceptsRequestId = false;

export const newRequestId = (): string => {
  const bytes = new Uint8Array(6);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

const isOurApi = (config: InternalAxiosRequestConfig): boolean => {
  const url = config.url ?? '';
  if (!/^https?:\/\//i.test(url)) return true; // relative to baseURL
  const api = (import.meta.env.VITE_APP_API_URL as string | undefined) ?? '';
  return Boolean(api) && url.startsWith(api);
};

const readHeader = (headers: unknown, name: string): string | undefined => {
  if (!headers || typeof headers !== 'object') return undefined;
  const h = headers as Record<string, unknown> & {
    get?: (key: string) => unknown;
  };
  const value =
    (typeof h.get === 'function' ? h.get(name) : undefined) ??
    h[name] ??
    h[name.toLowerCase()];
  return typeof value === 'string' && VALID_ID.test(value) ? value : undefined;
};

/**
 * Response step (success or failure): once the API has answered with an id,
 * it accepts one, so later calls send theirs.
 */
export const noteApiResponse = (response: unknown) => {
  if (apiAcceptsRequestId || !response || typeof response !== 'object') return;
  const headers = (response as { headers?: unknown }).headers;
  if (readHeader(headers, REQUEST_ID_HEADER)) apiAcceptsRequestId = true;
};

/** Headers to add by hand to a plain axios call to the API. */
export const requestIdHeaders = (): Record<string, string> =>
  apiAcceptsRequestId ? { [REQUEST_ID_HEADER]: newRequestId() } : {};

/** Request interceptor: add an id to a call to this app's API. */
export const attachRequestId = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  if (
    !apiAcceptsRequestId ||
    !isOurApi(config) ||
    readHeader(config.headers, REQUEST_ID_HEADER)
  ) {
    return config;
  }
  if (config.headers && typeof config.headers.set === 'function') {
    config.headers.set(REQUEST_ID_HEADER, newRequestId());
  } else {
    config.headers = {
      ...(config.headers ?? {}),
      [REQUEST_ID_HEADER]: newRequestId(),
    } as unknown as InternalAxiosRequestConfig['headers'];
  }
  return config;
};

/**
 * The id of a failed call: the one in the error body (the server's word),
 * else the response header, else the one this browser sent.
 */
export const requestIdFromError = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') return null;
  const e = error as {
    request_id?: unknown;
    error?: { request_id?: unknown };
    config?: { headers?: unknown };
    response?: { headers?: unknown; data?: unknown };
  };
  const fromBody = (body: unknown): string | undefined => {
    if (!body || typeof body !== 'object') return undefined;
    const b = body as {
      request_id?: unknown;
      error?: { request_id?: unknown };
    };
    const value = b.error?.request_id ?? b.request_id;
    return typeof value === 'string' && VALID_ID.test(value)
      ? value
      : undefined;
  };
  return (
    fromBody(e) ??
    fromBody(e.response?.data) ??
    readHeader(e.response?.headers, REQUEST_ID_HEADER) ??
    readHeader(e.config?.headers, REQUEST_ID_HEADER) ??
    null
  );
};

export const rememberFailedRequestId = (id: string | null | undefined) => {
  if (id) lastFailure = { id, at: Date.now() };
};

/** Response error interceptor step: keep the id of a failed call. */
export const rememberFailedRequest = (error: unknown) => {
  rememberFailedRequestId(requestIdFromError(error));
};

/** The id of a call that failed in the last few seconds, if any. */
export const recentFailedRequestId = (): string | null => {
  if (!lastFailure) return null;
  return Date.now() - lastFailure.at <= RECENT_FAILURE_MS
    ? lastFailure.id
    : null;
};

/**
 * The shared axios instance rejects with the response body, which loses the
 * request. Put the id on the body without making it a visible key, so code
 * that walks a body's keys (form errors) sees no change.
 */
export const tagBodyWithRequestId = <T>(body: T, error: unknown): T => {
  const id = requestIdFromError(error);
  if (id && body && typeof body === 'object' && !('request_id' in body)) {
    Object.defineProperty(body, 'request_id', {
      value: id,
      enumerable: false,
      configurable: true,
    });
  }
  return body;
};

/** "text (Ref: id)", or the text alone without an id. */
export const withRef = (text: string, id: string | null | undefined) =>
  id ? `${text} (Ref: ${id})` : text;

/** Tests only. */
export const resetRequestIdState = (accepts = false) => {
  lastFailure = null;
  apiAcceptsRequestId = accepts;
};
